import type { ReactNode } from "react";
import { Redirect, useLocation } from "wouter";
import { Loader2 } from "lucide-react";

import { AdminAgentConfigurationPanel } from "@/components/admin-agent-configuration-panel";
import { AdminPanel } from "@/components/lazy-heavy-components";
import { TenantAgentAdminPanel } from "@/components/tenant-agents-panel";
import { useAuthWithTestMode } from "@/hooks/use-auth-with-test-mode";
import {
  getTenantAgentEditorAccess,
  useMe,
  type TenantAgentEditorAccess,
} from "@/hooks/use-me";
import {
  agentConfigurationPath,
  agentConfigurationTabFromPath,
  DEFAULT_AGENT_CONFIGURATION_TAB,
  DEFAULT_TENANT_USAGE_TAB,
  isAgentConfigurationTab,
  isTenantUsageTab,
  tenantUsagePath,
  tenantUsagePathFromPath,
} from "@/lib/admin-route-tabs";
import { goBackToConversation } from "@/lib/in-app-history";
import type { AccessTokenGetter } from "@/lib/utils";

interface ResolvedAdminRoute {
  getToken: AccessTokenGetter;
  access: TenantAgentEditorAccess;
}

function RouteLoading() {
  return (
    <div className="flex h-full items-center justify-center bg-background text-foreground">
      <Loader2 className="size-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function ResolvedAdminRouteGate({
  getToken,
  children,
}: {
  getToken: AccessTokenGetter;
  children: (route: ResolvedAdminRoute) => ReactNode;
}) {
  const { me, loading } = useMe(getToken);

  if (loading) return <RouteLoading />;

  return children({
    getToken,
    access: getTenantAgentEditorAccess(me),
  });
}

/**
 * Shared authentication and identity gate for standalone admin routes.
 * The server remains authoritative for every underlying admin request.
 */
function AdminRouteGate({
  children,
}: {
  children: (route: ResolvedAdminRoute) => ReactNode;
}) {
  const { user, isLoading, getAccessToken } = useAuthWithTestMode();

  if (isLoading) return <RouteLoading />;
  if (!user) return <Redirect to="/" />;

  return (
    <ResolvedAdminRouteGate getToken={getAccessToken}>
      {children}
    </ResolvedAdminRouteGate>
  );
}

/** Platform-admin-only tenant administration route. */
export function TenantAdministrationPageRoute() {
  const [pathname] = useLocation();
  const tenantUsageRoute = tenantUsagePathFromPath(pathname);
  const canonicalTenantUsagePath = tenantUsageRoute && !isTenantUsageTab(tenantUsageRoute.tab)
    ? tenantUsagePath(tenantUsageRoute.tenantId, DEFAULT_TENANT_USAGE_TAB)
    : null;

  return (
    <AdminRouteGate>
      {({ getToken, access }) => (
        access.isPlatformAdmin
          ? canonicalTenantUsagePath
            ? <Redirect to={canonicalTenantUsagePath} replace />
            : <AdminPanel getToken={getToken} onBack={goBackToConversation} />
          : <Redirect to="/" />
      )}
    </AdminRouteGate>
  );
}

/**
 * Shared agent-configuration route. Platform admins can select a tenant;
 * tenant admins are locked to the verified tenant returned by /api/me.
 */
export function AgentConfigurationPageRoute() {
  const [pathname] = useLocation();
  const tab = agentConfigurationTabFromPath(pathname);
  const canonicalAgentConfigurationPath = !isAgentConfigurationTab(tab)
    ? agentConfigurationPath(DEFAULT_AGENT_CONFIGURATION_TAB)
    : null;

  return (
    <AdminRouteGate>
      {({ getToken, access }) => {
        if (canonicalAgentConfigurationPath) {
          return <Redirect to={canonicalAgentConfigurationPath} replace />;
        }

        if (access.isPlatformAdmin) {
          return <AdminAgentConfigurationPanel getToken={getToken} onBack={goBackToConversation} />;
        }

        return access.tenantId
          ? (
            <TenantAgentAdminPanel
              getToken={getToken}
              tenantId={access.tenantId}
              onBack={goBackToConversation}
            />
          )
          : <Redirect to="/" />;
      }}
    </AdminRouteGate>
  );
}
